import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/Layout/AppLayout';

const Login = lazy(() => import('./components/Auth/Login'));
const Register = lazy(() => import('./components/Auth/Register'));
const OAuthCallback = lazy(() => import('./components/Auth/OAuthCallback'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const PlayerList = lazy(() => import('./components/Players/PlayerList'));
const PlayerForm = lazy(() => import('./components/Players/PlayerForm'));
const PlayerDetail = lazy(() => import('./components/Players/PlayerDetail'));
const ReportList = lazy(() => import('./components/Reports/ReportList'));
const ReportForm = lazy(() => import('./components/Reports/ReportForm'));
const ReportDetail = lazy(() => import('./components/Reports/ReportDetail'));
const OrganizationDashboard = lazy(() => import('./components/Organizations/OrganizationDashboard'));
const UserProfile = lazy(() => import('./components/Profile/UserProfile'));
const PortfolioSettings = lazy(() => import('./components/PublicProfiles/PortfolioSettings'));
const PublicPortfolio = lazy(() => import('./components/PublicProfiles/PublicPortfolio'));
const PublicReportDetail = lazy(() => import('./components/PublicProfiles/PublicReportDetail'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Home/Landing Page (new design)
const Home = () => {
  const { isAuthenticated } = useAuth();
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-logo">
          <div className="landing-logo-icon">⚽</div>
          Nexus Scout
        </div>
        <div className="landing-nav-links">
          <Link to="/login" className="btn btn-ghost">Login</Link>
          <Link to="/register" className="btn btn-primary">Get Started</Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="landing-hero">
        <div className="landing-badge">
          ✨ Professional Football Scouting Platform
        </div>
        
        <h1 className="landing-title">
          Scout smarter.<br />
          Discover <span>elite talent</span>.
        </h1>
        
        <p className="landing-description">
          The command center for professional football scouting. 
          Track players, generate reports, and make data-driven decisions 
          with powerful analytics.
        </p>
        
        <div className="landing-cta">
          <Link to="/register" className="btn btn-primary btn-lg">
            Start Scouting — Free
          </Link>
          <Link to="/login" className="btn btn-secondary btn-lg">
            Sign In
          </Link>
        </div>
      </div>

      {/* Features */}
      <div className="landing-features">
        <div className="landing-feature">
          <div className="landing-feature-icon">📊</div>
          <h3>Player Database</h3>
          <p>Comprehensive profiles with detailed attributes, statistics, and career tracking.</p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">📋</div>
          <h3>Scout Reports</h3>
          <p>Professional reports with ratings, assessments, and actionable recommendations.</p>
        </div>
        <div className="landing-feature">
          <div className="landing-feature-icon">🏢</div>
          <h3>Team Collaboration</h3>
          <p>Manage your scouting organization with multiple scouts and admin controls.</p>
        </div>
      </div>
    </div>
  );
};

function AppContent() {
  return (
    <Router>
      <AppLayout>
        <Suspense
          fallback={
            <div className="loading">
              <div className="loading-spinner"></div>
              Loading...
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/oauth/callback" element={<OAuthCallback />} />

            <Route path="/u/:slug" element={<PublicPortfolio />} />
            <Route path="/u/:slug/reports/:reportId" element={<PublicReportDetail />} />
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/players" element={
              <ProtectedRoute>
                <PlayerList />
              </ProtectedRoute>
            } />
            
            <Route path="/players/new" element={
              <ProtectedRoute>
                <PlayerForm />
              </ProtectedRoute>
            } />

            <Route path="/players/edit/:id" element={
              <ProtectedRoute>
                <PlayerForm />
              </ProtectedRoute>
            } />
            
            <Route path="/players/:id" element={
              <ProtectedRoute>
                <PlayerDetail />
              </ProtectedRoute>
            } />
            
            <Route path="/reports" element={
              <ProtectedRoute>
                <ReportList />
              </ProtectedRoute>
            } />
            
            <Route path="/reports/new" element={
              <ProtectedRoute>
                <ReportForm />
              </ProtectedRoute>
            } />

            <Route path="/reports/edit/:id" element={
              <ProtectedRoute>
                <ReportForm />
              </ProtectedRoute>
            } />
            
            <Route path="/reports/:id" element={
              <ProtectedRoute>
                <ReportDetail />
              </ProtectedRoute>
            } />
            
            <Route path="/organization" element={
              <ProtectedRoute>
                <OrganizationDashboard />
              </ProtectedRoute>
            } />

            <Route path="/portfolio" element={
              <ProtectedRoute>
                <PortfolioSettings />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </AppLayout>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
